/* global axios */
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import LabelsAPI from '../../api/labels';
import types from '../mutation-types';

export const state = {
  records: [],
  hierarchy: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
  },
};

export const getters = {
  getLabels(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getLabelsOnSidebar(_state) {
    return _state.records
      .filter((record) => record.show_on_sidebar)
      .sort((a, b) => a.title.localeCompare(b.title));
  },
  getLabelsHierarchy(_state) {
    return _state.hierarchy
      .filter((hierarchy) => hierarchy.show_on_sidebar)
      .sort((a, b) => a.title.localeCompare(b.title));
  },
};

export const actions = {
  revalidate: async function revalidate({ commit }, { newKey }) {
    try {
      const isExistingKeyValid = await LabelsAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await LabelsAPI.refetchAndCommit(newKey);
        commit(types.SET_LABELS, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },

  get: async function getLabels({ commit }) {
    commit(types.SET_LABEL_UI_FLAG, { isFetching: true });
    try {
      const response = await LabelsAPI.get(false);
      const sortedLabels = response.data.payload.sort((a, b) =>
        a.title.localeCompare(b.title)
      );
      commit(types.SET_LABELS, sortedLabels);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isFetching: false });
    }
  },

  getHierarchy: async ({ commit }, accountId) => {
    try {
      const response = await axios.get(
        `/api/v1/accounts/${accountId}/labels/hierarchy`
      );
      commit(types.SET_LABELS_HIERARCHY, response.data.payload); // Assicurati di usare response.data se necessario
      return response.data.payload;
    } catch (error) {
      return [];
    }
  },

  create: async function createLabels({ commit, dispatch }, cannedObj) {
    const accountId = cannedObj.accountId;
    delete cannedObj.accountId;

    commit(types.SET_LABEL_UI_FLAG, { isCreating: true });
    try {
      const response = await LabelsAPI.create(cannedObj);
      commit(types.ADD_LABEL, response.data);

      if (accountId) {
        await dispatch('getHierarchy', accountId);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateLabels(
    { commit, dispatch },
    { id, ...updateObj }
  ) {
    const accountId = updateObj.accountId;
    delete updateObj.accountId;
    commit(types.SET_LABEL_UI_FLAG, { isUpdating: true });
    try {
      const response = await LabelsAPI.update(id, updateObj);
      commit(types.EDIT_LABEL, response.data);

      if (accountId) {
        await dispatch('getHierarchy', accountId);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteLabels({ commit, dispatch }, { id, accountId }) {
    commit(types.SET_LABEL_UI_FLAG, { isDeleting: true });
    try {
      await LabelsAPI.delete(id);
      commit(types.DELETE_LABEL, id);

      if (accountId) {
        await dispatch('getHierarchy', accountId);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_LABEL_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_LABELS]: MutationHelpers.set,
  [types.ADD_LABEL]: MutationHelpers.create,
  [types.EDIT_LABEL]: MutationHelpers.update,
  [types.DELETE_LABEL]: MutationHelpers.destroy,
  [types.SET_LABELS_HIERARCHY](_state, data) {
    _state.hierarchy = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
